import { resolveEntityDef } from './prefabs.ts';
import type { WorldData } from './schema.ts';

/**
 * Boot/respawn spawn resolution (ADR-0002): `player_start` is a generic framework class (logic
 * tier), so a world may place any number of named starts. PURE module — mount uses it for the
 * initial spawn, the fall respawn, AND the editor-Play respawn.
 *
 * Preference order:
 *   1. the player_start entity with id 'default' (first one, if several claim it)
 *   2. else the FIRST player_start placement
 *   3. else the world.spawn schema field (kept for back-compat), yaw 0
 * Placements resolve through the world's prefab table first, so a prefab extending player_start
 * counts.
 */
export function resolveSpawn(world: WorldData): {
  pos: [number, number, number];
  yawDeg: number;
} {
  let first: { pos: [number, number, number]; yawDeg: number } | null = null;
  for (const raw of world.entities) {
    const resolved = resolveEntityDef(raw, world.prefabs);
    if (!resolved || resolved.baseType !== 'player_start') continue;
    const d = resolved.def as Record<string, unknown>;
    const pos = d['pos'];
    if (
      !Array.isArray(pos) ||
      pos.length !== 3 ||
      !pos.every((n): n is number => typeof n === 'number')
    ) {
      continue; // malformed placement — the host's schema validation will warn about it
    }
    const out = {
      pos: [pos[0], pos[1], pos[2]] as [number, number, number],
      yawDeg: typeof d['yawDeg'] === 'number' ? d['yawDeg'] : 0,
    };
    const id = typeof d['id'] === 'string' ? d['id'] : 'default'; // schema default
    if (id === 'default') return out;
    if (!first) first = out;
  }
  return first ?? { pos: [world.spawn[0], world.spawn[1], world.spawn[2]], yawDeg: 0 };
}
